/**
 * Fonction throttle pour limiter la fréquence d'exécution d'une fonction
 * @param func - Fonction à throttler
 * @param delay - Délai minimum entre chaque exécution (en ms)
 * @returns Fonction throttlée avec méthode cancel()
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastExecTime = 0;

  const throttled = function (this: any, ...args: Parameters<T>) {
    const now = Date.now();
    const timeSinceLastExec = now - lastExecTime;

    const execute = () => {
      lastExecTime = now;
      func.apply(this, args);
    };

    if (timeSinceLastExec >= delay) {
      // Si assez de temps s'est écoulé, exécuter immédiatement
      execute();
    } else if (!timeoutId) {
      // Sinon, planifier l'exécution
      timeoutId = setTimeout(() => {
        execute();
        timeoutId = null;
      }, delay - timeSinceLastExec);
    }
  } as T & { cancel: () => void };

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return throttled;
}
